/**
 * Error Boundary Component
 *
 * React error boundary that catches JavaScript errors anywhere in the child
 * component tree, logs them to Sentry, and displays a fallback UI.
 *
 * Features:
 * - Catches React render errors
 * - Reports errors to Sentry with context
 * - Provides retry functionality
 * - User-friendly error display
 * - Route-level and component-level variants
 */

import * as React from "react";
import * as Sentry from "@sentry/react";
import { ErrorFallback, detectErrorType } from "./ErrorFallback";

export interface ErrorBoundaryProps {
  /** Child components to render */
  children: React.ReactNode;
  /** Custom fallback component */
  fallback?: React.ReactNode;
  /** Custom fallback render function */
  FallbackComponent?: React.ComponentType<{
    error: Error;
    resetErrorBoundary: () => void;
  }>;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Callback when error boundary is reset */
  onReset?: () => void;
  /** Keys that trigger a reset when changed */
  resetKeys?: unknown[];
  /** Context name for Sentry reporting */
  context?: string;
  /** Variant for the fallback UI */
  variant?: "default" | "compact" | "fullscreen";
  /** Show home button in fallback */
  showHomeButton?: boolean;
  /** Show report button in fallback */
  showReportButton?: boolean;
  /** Additional Sentry tags */
  sentryTags?: Record<string, string>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  isRetrying: boolean;
}

/**
 * Class-based error boundary component
 * (React requires class components for error boundaries)
 */
class ErrorBoundaryClass extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });

    // Report to Sentry
    const { context, sentryTags } = this.props;
    const errorType = detectErrorType(error);

    Sentry.withScope((scope) => {
      // Add context
      if (context) {
        scope.setTag("error_boundary_context", context);
      }

      // Add error type
      scope.setTag("error_type", errorType);

      // Add custom tags
      if (sentryTags) {
        Object.entries(sentryTags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      // Add component stack
      scope.setExtra("componentStack", errorInfo.componentStack);

      // Set level based on error type
      if (errorType === "network") {
        scope.setLevel("warning");
      } else {
        scope.setLevel("error");
      }

      // Capture the error
      Sentry.captureException(error);
    });

    // Call custom error handler
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset error state when reset keys change
    const { resetKeys } = this.props;
    if (this.state.hasError && resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, index) => prevProps.resetKeys?.[index] !== key
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  resetErrorBoundary = (): void => {
    this.setState({ isRetrying: true });

    // Simulate a brief delay for visual feedback
    setTimeout(() => {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        isRetrying: false,
      });
      this.props.onReset?.();
    }, 300);
  };

  handleReport = (): void => {
    const { error } = this.state;
    if (error) {
      // Open Sentry feedback dialog if available
      Sentry.showReportDialog({
        eventId: Sentry.lastEventId(),
        title: "Report an Issue",
        subtitle: "Our team has been notified. Want to help us fix it faster?",
        subtitle2: "Tell us what happened below.",
      });
    }
  };

  render(): React.ReactNode {
    const { hasError, error, isRetrying } = this.state;
    const {
      children,
      fallback,
      FallbackComponent,
      variant = "default",
      showHomeButton = true,
      showReportButton = false,
    } = this.props;

    if (hasError && error) {
      // Custom fallback component
      if (FallbackComponent) {
        return (
          <FallbackComponent
            error={error}
            resetErrorBoundary={this.resetErrorBoundary}
          />
        );
      }

      // Custom fallback element
      if (fallback) {
        return fallback;
      }

      // Default fallback
      return (
        <ErrorFallback
          error={error}
          errorType="render"
          resetErrorBoundary={this.resetErrorBoundary}
          isRetrying={isRetrying}
          variant={variant}
          showHomeButton={showHomeButton}
          showReportButton={showReportButton}
          onReport={this.handleReport}
        />
      );
    }

    return children;
  }
}

/**
 * Error Boundary wrapper with Sentry integration
 *
 * @example
 * // Basic usage
 * <ErrorBoundary context="HomePage">
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * @example
 * // With custom fallback
 * <ErrorBoundary
 *   context="DataTable"
 *   FallbackComponent={CustomErrorUI}
 * >
 *   <DataTable data={data} />
 * </ErrorBoundary>
 *
 * @example
 * // Route-level with fullscreen variant
 * <ErrorBoundary
 *   context="HoldingsRoute"
 *   variant="fullscreen"
 *   showHomeButton
 * >
 *   <HoldingsPage />
 * </ErrorBoundary>
 */
export function ErrorBoundary(props: ErrorBoundaryProps): React.ReactElement {
  return <ErrorBoundaryClass {...props} />;
}

/**
 * Wrapper using Sentry's error boundary for additional features
 * (profiling, replays, etc.)
 */
export function SentryErrorBoundary({
  children,
  context,
  ...props
}: ErrorBoundaryProps): React.ReactElement {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback
          error={error instanceof Error ? error : new Error(String(error))}
          resetErrorBoundary={resetError}
          errorType="render"
          variant={props.variant}
          showHomeButton={props.showHomeButton}
          showReportButton={props.showReportButton}
        />
      )}
      beforeCapture={(scope) => {
        if (context) {
          scope.setTag("error_boundary_context", context);
        }
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
